import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

import { PageMeta } from '../../components/common/PageMeta'
import { PhotoStrip } from '../../components/content/PhotoStrip'
import { OrderForm } from './OrderForm'
import { CardSkeletonGrid, ErrorState, Section } from '../../components/common/States'
import { servicesApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { useFormatters } from '../../hooks/useFormatters'
import type { ServiceCategory, ServiceItemDto } from '../../types/api'

/** Order the menu reads in, rather than alphabetically. A day starts with breakfast. */
const CATEGORY_ORDER: ServiceCategory[] = [
  'BREAKFAST',
  'DINNER',
  'BEVERAGE',
  'DESSERT',
  'HOUSEKEEPING',
  'EXPERIENCE',
  'OTHER',
]

export function ServicesPage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const theme = useTheme()
  const { money } = useFormatters()

  const services = useQuery({
    queryKey: ['services', locale],
    queryFn: () => servicesApi.menu(locale),
  })

  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    items: (services.data ?? []).filter((item) => item.category === category),
  })).filter((group) => group.items.length > 0)

  /** Price, or an honest "ask us" when the owner has not set one. */
  const priceLabel = (item: ServiceItemDto) => {
    if (!item.priceConfirmed || item.price == null) {
      return t('services.askUs')
    }
    const unit = {
      PER_PERSON: t('services.perPerson'),
      PER_ITEM: t('services.perItem'),
      PER_STAY: t('services.perStay'),
      PER_NIGHT: t('services.perNight'),
    }[item.unit]
    return `${money(item.price)} ${unit}`
  }

  const noticeLabel = (minutes: number) => {
    if (minutes >= 1440) return t('services.notice', { time: `${Math.round(minutes / 1440)} d` })
    if (minutes >= 60) return t('services.notice', { time: `${Math.round(minutes / 60)} h` })
    return t('services.notice', { time: `${minutes} min` })
  }

  return (
    <>
      <PageMeta title={t('services.title')} description={t('services.subtitle')} path="/services" />

      {/* The kitchen is the thing guests mention most often afterwards, and the page
          described it without showing any of it. */}
      <Section title={t('services.fromTheKitchen')}>
        <PhotoStrip category="FOOD" count={6} />
      </Section>

      {/* The API has taken orders since the first release and nothing on the site could place
          one: a guest read the menu, saw "order online", and had no way to do it. */}
      <Section>
        <OrderForm />
      </Section>

      {/* ---------------------------------------------------------------- how it works */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'common.white' }}>
        <Section
          title={t('services.title')}
          subtitle={t('services.subtitle')}
          sx={{
            '& h2': { color: 'common.white' },
            '& .MuiTypography-subtitle1': { color: alpha('#FFFFFF', 0.85) },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            }}
          >
            {[
              { n: '1', icon: '📅', title: t('services.step1'), body: t('services.step1Body') },
              { n: '2', icon: '💬', title: t('services.step2'), body: t('services.step2Body') },
              { n: '3', icon: '🍽️', title: t('services.step3'), body: t('services.step3Body') },
              { n: '4', icon: '🤝', title: t('services.step4'), body: t('services.step4Body') },
            ].map((step) => (
              <Box key={step.n}>
                <Box aria-hidden sx={{ fontSize: '1.9rem', mb: 1, lineHeight: 1 }}>
                  {step.icon}
                </Box>
                <Typography variant="overline" sx={{ opacity: 0.7, display: 'block' }}>
                  {step.n}
                </Typography>
                <Typography variant="h5" sx={{ mb: 0.75 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.8) }}>
                  {step.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>
      </Box>

      {/* ---------------------------------------------------------------- the menu */}
      <Section>
        {services.isPending && <CardSkeletonGrid count={6} height={200} />}
        {services.isError && (
          <ErrorState error={services.error} onRetry={() => void services.refetch()} />
        )}

        <Stack spacing={6}>
          {grouped.map((group) => (
            <Box key={group.category}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
                <Typography variant="h3" component="h2">
                  {t(`services.${group.category}`)}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                }}
              >
                {group.items.map((item) => (
                  <Card key={item.id} sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      aria-hidden
                      sx={{
                        fontSize: '1.75rem',
                        lineHeight: 1,
                        width: 54,
                        height: 54,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.09),
                      }}
                    >
                      {item.icon ?? '•'}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        spacing={1.5}
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="h5" component="h3">
                          {item.name}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            color: item.priceConfirmed ? 'primary.main' : 'text.secondary',
                            fontStyle: item.priceConfirmed ? 'normal' : 'italic',
                          }}
                        >
                          {priceLabel(item)}
                        </Typography>
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                        {item.summary}
                      </Typography>

                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                        {item.availableFrom && item.availableTo && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`🕐 ${t('services.served', {
                              from: item.availableFrom.slice(0, 5),
                              to: item.availableTo.slice(0, 5),
                            })}`}
                          />
                        )}
                        {item.advanceNoticeMinutes > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`⏳ ${noticeLabel(item.advanceNoticeMinutes)}`}
                          />
                        )}
                        <Chip
                          size="small"
                          color={item.orderableOnline ? 'success' : 'default'}
                          variant="outlined"
                          label={
                            item.orderableOnline
                              ? `📱 ${t('services.orderOnline')}`
                              : `🤝 ${t('services.askOwner')}`
                          }
                        />
                      </Stack>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>

        {/* The single most important sentence on the page. */}
        <Paper
          elevation={0}
          sx={{
            mt: 6,
            p: 3,
            borderRadius: 4,
            bgcolor: alpha(theme.palette.secondary.main, 0.1),
            borderInlineStart: `4px solid ${theme.palette.secondary.main}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            💶 {t('services.noPaymentOnline')}
          </Typography>
        </Paper>
      </Section>
    </>
  )
}
